import React, { useRef, useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { createUserWithEmailAndPassword, signInWithPopup, GoogleAuthProvider } from "firebase/auth";
import { auth, db } from "../contexts/firebase";
import { setDoc, doc, getDoc, serverTimestamp } from "firebase/firestore";
import { EyeIcon, EyeOffIcon } from "@heroicons/react/outline";

export default function Signup() {
  const navigate = useNavigate();

  // refs
  const firstNameRef = useRef();
  const lastNameRef = useRef();
  const emailRef = useRef();
  const dobRef = useRef();
  const passwordRef = useRef();
  const confirmPasswordRef = useRef();
  const YOUR_BREVO_API_KEY = "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"
  const [termsAgreed, setTermsAgreed] = useState(false);

  // state
  const [errors, setErrors] = useState({});  // per-field
  const [submitError, setSubmitError] = useState(""); // server/Firebase
  const [loading, setLoading] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [toast, setToast] = useState({ message: "", type: "" });

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        // User is already logged in, redirect them
        navigate("/dashboard"); // or "/generate" or any other page you want
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  // iOS zoom fix + resize listener
  useEffect(() => {
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    if (isIOS) {
      const vp = document.querySelector("meta[name=viewport]");
      const orig = vp?.getAttribute("content");
      if (vp && orig) {
        vp.setAttribute("content", orig + ", maximum-scale=1");
        setTimeout(() => vp.setAttribute("content", orig), 500);
      }
    }
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const backgroundImage = isMobile
    ? "https://geneartai.com/images/mobile1.png"
    : "https://geneartai.com/images/image1.png";

  // helper to render a field’s error
  const FieldError = ({ field }) =>
    errors[field] ? (
      <p className="text-red-500 text-sm mt-1">{errors[field]}</p>
    ) : null;

  const sendWelcomeEmail = async (email, firstName) => {
    try {
      await fetch("/api/send-welcome-email", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, firstName }),
      });
    } catch (err) {
      console.error("Welcome email error:", err);
    }
  };
  const handleGoogleSignIn = async () => {
    const provider = new GoogleAuthProvider();

    try {
      const result = await signInWithPopup(auth, provider);
      const user = result.user;

      // Check if user already exists
      const userRef = doc(db, "users", user.uid);
      const docSnap = await getDoc(userRef);

      if (!docSnap.exists()) {
        // Add new user to Firestore
        await setDoc(userRef, {
          email: user.email,
          name: user.displayName,
          credits: 20,
          createdAt: serverTimestamp(),
        });
      }

      setToast({ message: "Signed in successfully 🎉", type: "success" });
      setTimeout(() => {
        window.location.href = "/generate";
      }, 1000);

    } catch (error) {
      console.error("Google Sign-In Error:", error);
      setToast({ message: "Google Sign-In failed. Please try again.", type: "error" });
    }
  };

  // const sendWelcomeEmail = async (email, firstName) => {
  //   try {
  //     await fetch('https://api.brevo.com/v3/smtp/email', {
  //       method: 'POST',
  //       headers: {
  //         'accept': 'application/json',
  //         'api-key': YOUR_BREVO_API_KEY,
  //         'content-type': 'application/json'
  //       },
  //       body: JSON.stringify({
  //         to: [{ email: email, name: firstName }],
  //         templateId: 1,
  //         params: {
  //           FIRSTNAME: firstName, // Match the placeholders you used in the template
  //         }
  //       })
  //     });
  //   } catch (error) {
  //     console.error("Failed to send Welcome Email:", error);
  //   }
  // };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});
    setSubmitError("");
    setLoading(true);

    // 1) Collect values
    const firstName = firstNameRef.current.value.trim();
    const lastName = lastNameRef.current.value.trim();
    const email = emailRef.current.value.trim();
    const dob = dobRef.current.value;
    const pwd = passwordRef.current.value;
    const confirm = confirmPasswordRef.current.value;

    // 2) Validate
    const newErrors = {};
    if (!firstName) newErrors.firstName = "First name is required.";
    if (!lastName) newErrors.lastName = "Last name is required.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))
      newErrors.email = "Please enter a valid email.";
    if (!dob) newErrors.dob = "Date of birth is required.";

    // password rules: ≥8 chars, upper, lower, digit, special
    const pwdRegex =
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/;
    if (!pwdRegex.test(pwd))
      newErrors.password =
        "Password must be ≥8 chars and include uppercase, lowercase, number, & special char.";
    if (pwd !== confirm)
      newErrors.confirmPassword = "Passwords do not match.";

    if (!termsAgreed) {
      newErrors.terms = "You must agree to the Terms and Conditions.";
    }

    if (Object.keys(newErrors).length) {
      setErrors(newErrors);
      setLoading(false);
      return;
    }

    // 3) Firebase signup
    try {
      const { user } = await createUserWithEmailAndPassword(auth, email, pwd);
      await setDoc(doc(db, "users", user.uid), {
        uid: user.uid,
        firstName,
        lastName,
        email,
        dob,
        credits: 20, // 20 free credits for every new signup
        termsAgreed: termsAgreed,
        createdAt: new Date(),
      });
      await sendWelcomeEmail(email, firstName); // Send welcome email
      navigate("/dashboard");
    } catch (err) {
      setSubmitError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen font-sans text-white overflow-x-hidden">
      {/* Background */}
      <div
        className="fixed inset-0 bg-cover bg-center z-0"
        style={{
          backgroundImage: `url('${backgroundImage}')`,
          backgroundAttachment: isMobile ? "scroll" : "fixed",
        }}
      >
        <div className="absolute inset-0 bg-black/20" />
      </div>
      {toast.message && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ message: "", type: "" })}
          duration={4000}
        />
      )}

      {/* Form */}
      <div className="relative z-10 flex flex-col min-h-screen">
        <div className="flex-grow flex items-center justify-center px-4 py-8 pt-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="bg-black/20 backdrop-blur-md p-8 rounded-2xl shadow-2xl w-full max-w-md"
          >
            {/* 🎁 20 Free Credits Banner */}
            <div className="bg-yellow-400 text-yellow-900 font-bold text-sm rounded-full py-2 px-4 text-center mb-6 shadow-md">
              🎁 20 Credits on Signup!
            </div>
            <h2 className="text-3xl font-bold mb-6 text-center">
              Create Your Account
            </h2>

            {/* server / Firebase errors */}
            {submitError && (
              <div className="bg-red-100 text-red-700 text-sm p-2 rounded mb-4 text-center">
                {submitError}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* First Name */}
              <div>
                <label className="block text-base font-semibold mb-1 text-gray-200">
                  First Name
                </label>
                <input
                  ref={firstNameRef}
                  type="text"
                  placeholder="First name"
                  className="w-full px-4 py-2 rounded-lg bg-black/30 border border-gray-600 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-white text-base"
                />
                <FieldError field="firstName" />
              </div>

              {/* Last Name */}
              <div>
                <label className="block text-base font-semibold mb-1 text-gray-200">
                  Last Name
                </label>
                <input
                  ref={lastNameRef}
                  type="text"
                  placeholder="Last name"
                  className="w-full px-4 py-2 rounded-lg bg-black/30 border border-gray-600 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-white text-base"
                />
                <FieldError field="lastName" />
              </div>

              {/* Email */}
              <div>
                <label className="block text-base font-semibold mb-1 text-gray-200">
                  Email
                </label>
                <input
                  ref={emailRef}
                  type="email"
                  placeholder="you@example.com"
                  className="w-full px-4 py-2 rounded-lg bg-black/30 border border-gray-600 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-white text-base"
                />
                <FieldError field="email" />
              </div>

              {/* DOB */}
              <div>
                <label className="block text-base font-semibold mb-1 text-gray-200">
                  Date of Birth
                </label>
                <input
                  ref={dobRef}
                  type="date"
                  className="w-full px-4 py-2 rounded-lg bg-black/30 border border-gray-600 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-white text-base"
                />
                <FieldError field="dob" />
              </div>

              {/* Password */}
              <div>
                <label className="block text-base font-semibold mb-1 text-gray-200">
                  Password
                </label>
                <div className="relative">
                  <input
                    ref={passwordRef}
                    type={showPwd ? "text" : "password"}
                    placeholder="Create a password"
                    className="w-full px-4 py-2 rounded-lg bg-black/30 border border-gray-600 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-white text-base"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPwd((v) => !v)}
                    className="absolute inset-y-0 right-3 flex items-center text-gray-300"
                    aria-label={showPwd ? "Hide password" : "Show password"}
                  >
                    {showPwd ? (
                      <EyeOffIcon className="h-5 w-5" />
                    ) : (
                      <EyeIcon className="h-5 w-5" />
                    )}
                  </button>
                </div>
                <FieldError field="password" />
              </div>

              {/* Confirm Password */}
              <div>
                <label className="block text-base font-semibold mb-1 text-gray-200">
                  Confirm Password
                </label>
                <div className="relative">
                  <input
                    ref={confirmPasswordRef}
                    type={showConfirm ? "text" : "password"}
                    placeholder="Confirm your password"
                    className="w-full px-4 py-2 rounded-lg bg-black/30 border border-gray-600 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-white text-base"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirm((v) => !v)}
                    className="absolute inset-y-0 right-3 flex items-center text-gray-300"
                    aria-label={showConfirm ? "Hide password" : "Show password"}
                  >
                    {showConfirm ? (
                      <EyeOffIcon className="h-5 w-5" />
                    ) : (
                      <EyeIcon className="h-5 w-5" />
                    )}
                  </button>
                </div>
                <FieldError field="confirmPassword" />
              </div>

              {/* Terms and Conditions */}
              <div className="flex flex-col gap-1 mt-2">
                <div className="flex items-start gap-2">
                  <input
                    id="terms"
                    type="checkbox"
                    checked={termsAgreed}
                    onChange={(e) => setTermsAgreed(e.target.checked)}
                    className="mt-1 accent-[#166CB0] w-4 h-4"
                  />
                  <label htmlFor="terms" className="text-sm text-gray-300 leading-tight">
                    I agree to the{" "}
                    <Link to="/terms-of-service" className="underline hover:text-white">
                      Terms of Service
                    </Link>{" "}
                    and{" "}
                    <Link to="/privacy-policy" className="underline hover:text-white">
                      Privacy Policy
                    </Link>
                    .
                  </label>
                </div>

                <FieldError field="terms" />
              </div>


              {/* Submit */}
              <button
                type="submit"
                disabled={loading}
                className={`w-full py-2.5 rounded-full font-semibold text-base ${loading
                  ? "bg-gray-400 text-gray-600 cursor-not-allowed"
                  : "bg-[#166CB0] hover:bg-blue-800 text-white"
                  } transition duration-300 flex justify-center items-center gap-2`}
              >
                {loading ? (
                  <svg
                    className="animate-spin h-5 w-5 text-white"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8v8H4z"
                    />
                  </svg>
                ) : (
                  "Create Account"
                )}
              </button>

            </form>

            <button
              onClick={handleGoogleSignIn}
              className="mt-4 w-full py-2.5 rounded-full font-semibold text-base bg-white text-black hover:bg-gray-200 transition duration-300 flex justify-center items-center gap-2 shadow-md"
            >
              <img src="/google.png" alt="Google" className="w-5 h-5" />
              Continue with Google
            </button>


            <p className="text-center text-sm text-gray-300 mt-4">
              Already have an account?{" "}
              <button onClick={() => navigate("/login")} className="text-white underline">
                Login
              </button>
            </p>
          </motion.div>
        </div>
        {/* Footer */}
        <footer className="text-center text-xs text-gray-300 pb-3 mt-auto">
          <div>© 2025 GeneArtAI — All Rights Reserved.</div>
          <div className="mt-2">
            <Link to="/privacy-policy" className="text-gray-300 hover:text-white underline">
              Privacy Policy
            </Link>{" "}
            |{" "}
            <Link to="/terms-of-service" className="underline hover:text-white">
              Terms of Service
            </Link>
          </div>
        </footer>

        {/* Spacer for bottom ad */}
        <div className="block sm:hidden h-20" />
      </div>
    </div>
  );
}
